import { ApiEndpoints } from "@shared/api/api-endpoints";
import {
  onboardingApiClient,
  authApiClient,
} from "@shared/api/clients/apiClientFactory";
import { transformJsonToObject } from "@shared/utils/jsonToObjectTransformer";

export const PlanApi = {
  getBillingFrequency: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/billingFrequency",
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getIncludedIn: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/include_in",
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getCategories: async (remoteUtilityId: string): Promise<any[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "category");
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any[]>(response.data.result);
  },
  getSubcategories: async (
    remoteUtilityId: string,
    categoryCode: string,
  ): Promise<any[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "config-map/category_json",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        query.push("config_level", "sub_category");
        if (categoryCode) {
          const codes = categoryCode.split(",");
          codes.forEach((code) => {
            if (code.trim()) {
              query.push("code_list", code.trim());
            }
          });
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any[]>(response.data.result);
  },
  getContractsData: async (
    remoteUtilityId: string,
    categories?: string[],
    subCategories?: string[],
  ): Promise<any[]> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "contract",
      (query) => {
        query.push("remote_utility_id", remoteUtilityId);
        if (categories && categories.length > 0) {
          categories.forEach((cat) => query.push("categories", cat));
        }
        if (subCategories && subCategories.length > 0) {
          subCategories.forEach((sub) => query.push("sub_categories", sub));
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return transformJsonToObject<any[]>(response.data.result);
  },
  createPlan: async (payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "v1/plan",
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },
  updatePlan: async (planId: string | number, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planId}`,
    );
    const response = await onboardingApiClient.put(url, payload);
    return response.data;
  },
  getUtilityServices: async (utilityId: string): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "auth",
      "utility/services",
      (query) => {
        if (utilityId) query.push("utility_id", utilityId);
      },
    );
    const response = await authApiClient.get(url);
    return response.data;
  },
  getRate: async (
    remoteUtilityId: string,
    utilityService: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "rate-v1",
      (query) => {
        if (remoteUtilityId) query.push("remote_utility_id", remoteUtilityId);
        if (utilityService) query.push("utility_service", utilityService);
        query.push("no_pagination_required", "1");
        query.push("status_display", "Active");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getParameter: async (
    remoteUtilityId: string,
    utilityService: string | string[],
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "v1/parameter",
      (query) => {
        if (remoteUtilityId) query.push("remote_utility_id", remoteUtilityId);
        if (utilityService) {
          if (Array.isArray(utilityService)) {
            utilityService.forEach((service) =>
              query.push("utility_service", service),
            );
          } else {
            query.push("utility_service", utilityService);
          }
        }
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getPlanDetail: async (
    planId: string | number,
    remoteUtilityId: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planId}/detail`,
      (query) => {
        if (remoteUtilityId) query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  //
  getPlanDetailByTab: async (
    planID: string,
    remoteUtilityId: string,
    tab: string,
  ) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planID}/detail/${tab}`,
      (query) => {
        if (remoteUtilityId) query.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  clonePlan: async (planID: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planID}/clone`,
    );
    const response = await onboardingApiClient.post(url);
    return response.data;
  },
  getParameterValues: async (remoteUtilityId: string, parameterId: string) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `parameter-values-fetch`,
      (query) => {
        if (remoteUtilityId) query.push("remote_utility_id", remoteUtilityId);
        if (parameterId) query.push("parameter_id", parameterId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  publishPlan: async (planID: string, payload: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planID}/publish`,
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },
  getPlanList: async (params: any) => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "v1/plan",
      (query) => {
        if (params.remote_utility_id)
          query.push("remote_utility_id", params.remote_utility_id);
        if (params.is_pagination_required !== undefined)
          query.push("is_pagination_required", params.is_pagination_required);
        if (params.search_data) query.push("search_data", params.search_data);
        if (params.page) query.push("page", params.page);
        if (params.limit) query.push("limit", params.limit);
        if (params.category) query.push("category", params.category);
        if (params.utilityService)
          query.push("utility_service", params.utilityService);
        query.push("is_pagination_required", "1");
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },
  getPlanListForEdit: async (
    remoteUtilityId: string,
    filters?: any,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      "v1",
      ["plan"],
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("is_pagination_required", "1");
        qs.push("is_bulk_edit_list", "1");

        if (filters?.page) qs.push("page", filters.page.toString());
        if (filters?.limit) qs.push("limit", filters.limit.toString());
        if (filters?.search_data) qs.push("search_data", filters.search_data);
        if (filters?.category) qs.push("category", filters.category);
        if (filters?.utilityService)
          qs.push("utility_service", filters.utilityService);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  previewPlanEditDetails: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `v1`,
      ["plan", "bulk", "preview"],
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  applyPlanEditDetails: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `v1`,
      ["plan", "bulk", "apply"],
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data;
  },

  getPlanChargesChoices: async (): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      "choices/plan_charges",
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  applyCommonServices: async (data: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `v1`,
      ["plan", "bulk", "common-services"],
    );
    const response = await onboardingApiClient.post(url, data);
    return response.data.result;
  },
  getPlanVersionList: async (
    planID: string,
    remoteUtilityId: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `v2`,
      ["plan", planID, "versions"],
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  getPlanVersionDetail: async (
    planID: string,
    remoteUtilityId: string,
    versionID: string,
  ): Promise<any> => {
    const url = ApiEndpoints.createUrlWithPathAndQueryVariables(
      "onboarding",
      `v2`,
      ["plan", planID, "versions"],
      (qs) => {
        qs.push("remote_utility_id", remoteUtilityId);
        qs.push("version_id", versionID);
      },
    );
    const response = await onboardingApiClient.get(url);
    return response.data;
  },

  schedulePlan: async (planId: string, payload: any): Promise<any> => {
    const url = ApiEndpoints.createUrlWithQueryParameters(
      "onboarding",
      `v1/plan/${planId}/publish`,
    );
    const response = await onboardingApiClient.post(url, payload);
    return response.data;
  },
};
